/*
 * File: SuggestedQuestionsWidget.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 29, 2025 at 1:13 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDebouncedCallback } from 'beautiful-react-hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';

// Services
import { searchQuestions } from '../../services/question';
import { recordAction } from '../../services/action';

// Components
import { LocaleLink } from '../LocaleLink';
import { Typography } from '../Typography';
import { ObfuscatedText } from '../ObfuscatedText';

// Constants
import { SOURCES, USER_ACTIONS } from '../../../constants/base.constants';

// Styles
import * as S from './SuggestedQuestionsWidget.styles';


/**
 * Component
 */

export const SuggestedQuestionsWidget = memo(({
	className, prompt, onClick
}) => {

	// Create state
	const [suggestedQuestions, setSuggestedQuestions] = useState(prompt);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Handle debounce function for updating suggestions
	const debounceUpdateSuggestions = useDebouncedCallback(async (p) => {

		// Clear suggested questions if necessary
		if (!p || p.length < 1) {
			setSuggestedQuestions([]);
			return;
		}

		// Update suggested questions
		const { data: { result: { questions } } } = await searchQuestions({
			searchText: p,
			urlPath: window.location.pathname,
			source: SOURCES.QUESTIONS
		});

		// Set suggested questions
		setSuggestedQuestions(questions);

	}, [], 600);

	// Handle suggestion click
	const onSuggestionClick = (question) => {

		// Record action
		recordAction({
			action: USER_ACTIONS.SEARCHED_QUESTION_SUGGESTION_CLICK,
			urlPath: window.location.pathname,
			context: {
				search_text: prompt,
				question_id: question.question_id
			}
		});

		// Handle on click function if necessary
		if (onClick) onClick();
	};

	// Handle actions on prompt update
	useEffect(() => {

		// Update suggestions
		debounceUpdateSuggestions(prompt);

	}, [debounceUpdateSuggestions, prompt, onClick]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<S.Wrapper className={className}>
			{suggestedQuestions?.map((question) => (
				<LocaleLink to={`/answer/${question.slug}`} onClick={() => { onSuggestionClick(question); }} key={question.question_id}>
					<S.SuggestedQuestion>
						<S.GrowClampContent clamp={2}>
							<Typography weight="regular" tag="p" variation="2">
								<ObfuscatedText>
									{question?.question_locales?.[locale.localeBaseId]}
								</ObfuscatedText>
							</Typography>
						</S.GrowClampContent>
						<FontAwesomeIcon icon={['fas', 'arrow-up-right']} />
					</S.SuggestedQuestion>
				</LocaleLink>
			))}
		</S.Wrapper>
	);
});


/**
 * Configuration
 */

SuggestedQuestionsWidget.displayName = 'SuggestedQuestionsWidget';
SuggestedQuestionsWidget.propTypes = {
	className: PropTypes.string,
	prompt: PropTypes.string,
	onClick: PropTypes.func
};
SuggestedQuestionsWidget.defaultProps = {
	className: null,
	prompt: null,
	onClick: null
};
